import type { ComponentProps, ReactNode } from 'react';
import { createElement, isValidElement, useState } from 'react';
import { cn } from '@savvy-kit/lib/utils';
import { isNil } from 'es-toolkit';
import { Check, ChevronRight, Minus, Search } from 'lucide-react';

import type { Column, ColumnOption, DataTableFilterActions, FilterModel, FiltersState } from '../core/types';
import { FilterValueNumberController } from './filter-value';

type FilterOptionState = 'off' | 'included' | 'excluded';

// Each cascade level is its own floating card, matching the group list / options
// split the FilterSelector already uses between the dimension and options panes.
// A local component rather than the kit <Card>: that one is a padded rounded-xl
// content card, whereas this is a compact scrollable dropdown pane.
function FilterOptionCard({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex max-h-60 min-w-[200px] max-w-[280px] flex-col overflow-y-auto rounded-md border border-light bg-white shadow-md',
        className,
      )}
      {...props}
    />
  );
}

function FilterOptionCheckbox({ state, value }: { readonly state: FilterOptionState; readonly value: string }) {
  if (state === 'included') {
    return (
      <span
        data-testid={`option-check-${value}`}
        aria-hidden
        className="pointer-events-none inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-primary bg-primary text-white"
      >
        <Check className="size-3 shrink-0" />
      </span>
    );
  }
  if (state === 'excluded') {
    return (
      <span
        data-testid={`option-neq-${value}`}
        aria-hidden
        className="pointer-events-none inline-flex size-4 shrink-0 items-center justify-center rounded-[4px] border border-destructive bg-destructive text-white"
      >
        <Minus className="size-3 shrink-0" />
      </span>
    );
  }
  return (
    <span
      aria-hidden
      className="pointer-events-none inline-flex size-4 shrink-0 rounded-[4px] border border-input bg-white"
    />
  );
}

type FilterOptionsPanelProps<TData> = {
  readonly column: Column<TData, 'option' | 'multiOption' | 'number'>;
  readonly filters: FiltersState;
  readonly actions: DataTableFilterActions;
  readonly searchable?: boolean;
};

// Distinct groups in first-appearance order, plus the options belonging to each.
function getOptionGroups(options: readonly ColumnOption[]) {
  const order: string[] = [];
  const byValue = new Map<string, { label: string; options: ColumnOption[] }>();
  for (const option of options) {
    if (isNil(option.group)) continue;
    const { value, label } = option.group;
    let bucket = byValue.get(value);
    if (isNil(bucket)) {
      bucket = { label, options: [] };
      byValue.set(value, bucket);
      order.push(value);
    }
    bucket.options.push(option);
  }
  return order.map((value) => ({ value, label: byValue.get(value)!.label, options: byValue.get(value)!.options }));
}

function SearchInput({
  query,
  setQuery,
  placeholder,
}: {
  readonly query: string;
  readonly setQuery: (value: string) => void;
  readonly placeholder: string;
}) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-light">
      <Search className="size-4 text-muted-foreground shrink-0" />
      <input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="text-sm outline-none w-full bg-transparent placeholder:text-muted-foreground"
      />
    </div>
  );
}

export function FilterOptionsPanel<TData>({
  column,
  filters,
  actions,
  searchable = false,
}: FilterOptionsPanelProps<TData>) {
  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<string | null>(null);

  if (column.type === 'number') {
    const numberColumn = column as Column<TData, 'number'>;
    const numberFilter = filters.find(
      (filter): filter is FilterModel<'number'> => filter.columnId === numberColumn.id && filter.type === 'number',
    );
    const facetedMinMax = numberColumn.getFacetedMinMaxValues();
    const defaultFilter: FilterModel<'number'> = {
      columnId: numberColumn.id,
      type: 'number',
      operator: 'is between',
      values: !isNil(facetedMinMax) ? [facetedMinMax[0], facetedMinMax[1]] : [0, 0],
    };

    return (
      <div className="w-[300px]">
        <FilterValueNumberController
          filter={!isNil(numberFilter) ? numberFilter : defaultFilter}
          column={numberColumn}
          actions={actions}
          strategy="server"
        />
      </div>
    );
  }

  const optionColumn = column as Column<TData, 'option' | 'multiOption'>;
  const allOptions = optionColumn.getOptions();
  const filterModel = filters.find((f) => f.columnId === optionColumn.id);
  const supportsNegation = (optionColumn as Column<TData, 'multiOption'>).supportsValueNegation === true;
  const isMultiOption = optionColumn.type === 'multiOption';
  const searchPlaceholder = `Search ${optionColumn.displayName.toLowerCase()}...`;

  const searching = searchable && query.length > 0;
  // Search flattens across groups — matches feel faster than drilling to find one.
  const searchMatches = searching
    ? allOptions.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : allOptions;
  // Drill-in mode requires EVERY option to carry a group — a mix of grouped and
  // ungrouped options would drop the ungrouped ones (they have no group card to
  // live in), so fall back to a flat list in that case.
  const grouped = allOptions.length > 0 && allOptions.every((o) => !isNil(o.group));

  function renderOptionRow(option: ColumnOption) {
    const isSelected = !isNil(filterModel) && filterModel.values.includes(option.value);
    const isIncluded = isSelected;
    const isExcluded =
      supportsNegation && !isNil(filterModel) && (filterModel.excludedValues ?? []).includes(option.value);
    // Matches FilterValue's element-or-component handling. A `typeof === 'function'` test would
    // miss forwardRef components — every lucide icon — and render the object as a child.
    const iconEl = isNil(option.icon)
      ? null
      : isValidElement(option.icon)
        ? option.icon
        : createElement(option.icon, { className: 'size-4 shrink-0' });
    const optionState: FilterOptionState = isExcluded ? 'excluded' : isIncluded ? 'included' : 'off';
    const isDisabled = option.disabled === true;

    const handleRowClick = () => {
      if (isDisabled) return;
      if (supportsNegation) {
        // Excluded rows go to included on row click (not off). Clearing an exclusion is
        // done via the hover-reveal button or the chip X — this is per the v1-F spec.
        if (isIncluded) {
          actions.clearFilterValue(optionColumn as Column<TData, 'multiOption'>, option.value);
        } else {
          actions.addFilterValue(optionColumn, [option.value]);
        }
        return;
      }
      if (isSelected) {
        actions.removeFilterValue(optionColumn, [option.value]);
      } else {
        actions.addFilterValue(optionColumn, [option.value]);
      }
    };

    const handleExcludeClick = (event: { stopPropagation: () => void }) => {
      event.stopPropagation();
      if (!supportsNegation) return;
      if (isExcluded) {
        actions.clearFilterValue(optionColumn as Column<TData, 'multiOption'>, option.value);
        return;
      }
      actions.excludeFilterValue(optionColumn as Column<TData, 'multiOption'>, [option.value]);
    };

    return (
      <div
        key={option.value}
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-label={option.label}
        aria-disabled={isDisabled ? true : undefined}
        className={cn(
          'group flex items-center justify-between gap-4 px-3 py-1.5 text-sm w-full transition-colors',
          isDisabled ? 'cursor-default text-muted-foreground' : 'cursor-pointer',
          isExcluded ? 'bg-destructive/5 hover:bg-destructive/10' : !isDisabled && 'hover:bg-component-hover',
          option.indent === true && 'pl-8',
        )}
        onClick={handleRowClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleRowClick();
          }
        }}
      >
        <span className="flex items-center gap-2 min-w-0">
          <FilterOptionCheckbox state={optionState} value={option.value} />
          {iconEl}
          <span className="truncate">{option.label}</span>
        </span>
        {supportsNegation && !isDisabled && (
          <button
            type="button"
            aria-label={isExcluded ? `Remove exclusion for ${option.label}` : `Exclude ${option.label}`}
            data-testid={`option-exclude-toggle-${option.value}`}
            className={cn(
              'inline-flex shrink-0 items-center justify-center rounded-full px-3 py-1 text-xs font-medium',
              'cursor-pointer bg-foreground/8 text-foreground hover:bg-foreground/15',
              'transition-opacity',
              'opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus-visible:opacity-100',
            )}
            onClick={handleExcludeClick}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                event.stopPropagation();
                handleExcludeClick(event);
              }
            }}
          >
            {isExcluded ? 'Remove' : 'Exclude'}
          </button>
        )}
      </div>
    );
  }

  // Flat column — bare content; the FilterSelector wraps it in a card.
  if (!grouped) {
    return (
      <div className="flex flex-col min-w-[200px] max-w-[280px]">
        {searchable && <SearchInput query={query} setQuery={setQuery} placeholder={searchPlaceholder} />}
        <div className="py-1">{searchMatches.map(renderOptionRow)}</div>
      </div>
    );
  }

  // Grouped column — a cascade of self-contained cards: the group list, and (once
  // a group is picked) that group's options in a card to its right. Searching
  // collapses to a flat match list in the first card.
  const groups = getOptionGroups(allOptions);
  const active = activeGroup !== null ? groups.find((g) => g.value === activeGroup) : undefined;
  const selectedValues = filterModel?.values ?? [];

  return (
    <div className="flex gap-2 items-start">
      <FilterOptionCard>
        {searchable && <SearchInput query={query} setQuery={setQuery} placeholder={searchPlaceholder} />}
        {searching ? (
          <div className="py-1">{searchMatches.map(renderOptionRow)}</div>
        ) : (
          <div className="py-1">
            {groups.map((g) => {
              const selected = g.options.filter((o) => selectedValues.includes(o.value)).length;
              const isActive = active?.value === g.value;
              return (
                <div
                  key={g.value}
                  role="button"
                  tabIndex={0}
                  aria-label={g.label}
                  aria-expanded={isActive}
                  className={cn(
                    'group flex items-center justify-between gap-2 px-3 py-1.5 text-sm w-full cursor-pointer transition-colors hover:bg-component-hover',
                    isActive && 'bg-component-hover',
                  )}
                  onClick={() => setActiveGroup(g.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setActiveGroup(g.value);
                    }
                  }}
                >
                  <span className="truncate">{g.label}</span>
                  <span className="flex items-center gap-1.5 shrink-0 text-muted-foreground">
                    {selected > 0 && (
                      <Check data-testid={`group-selected-${g.value}`} className="size-4 text-primary" />
                    )}
                    <ChevronRight className="size-4" />
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </FilterOptionCard>
      {!searching && !isNil(active) && (
        <GroupOptionsCard
          group={active}
          selectedValues={selectedValues}
          isMultiOption={isMultiOption}
          renderOptionRow={renderOptionRow}
          onToggleGroup={(values, allSelected) => {
            if (allSelected) actions.removeFilterValue(optionColumn, values);
            else actions.addFilterValue(optionColumn, values);
          }}
        />
      )}
    </div>
  );
}

function GroupOptionsCard({
  group,
  selectedValues,
  isMultiOption,
  renderOptionRow,
  onToggleGroup,
}: {
  readonly group: { value: string; label: string; options: ColumnOption[] };
  readonly selectedValues: readonly string[];
  readonly isMultiOption: boolean;
  readonly renderOptionRow: (option: ColumnOption) => ReactNode;
  readonly onToggleGroup: (values: string[], allSelected: boolean) => void;
}) {
  const toggleable = group.options.filter((o) => o.disabled !== true);
  const values = toggleable.map((o) => o.value);
  const selectedInGroup = toggleable.filter((o) => selectedValues.includes(o.value)).length;
  const allSelected = selectedInGroup === toggleable.length && toggleable.length > 0;

  return (
    <FilterOptionCard>
      <div className="py-1">
        {isMultiOption && (
          <div
            role="button"
            tabIndex={0}
            aria-label={`Select all ${group.label}`}
            data-testid={`group-select-all-${group.value}`}
            className="flex items-center gap-2 px-3 py-1.5 text-sm w-full cursor-pointer font-medium transition-colors hover:bg-component-hover"
            onClick={() => onToggleGroup(values, allSelected)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onToggleGroup(values, allSelected);
              }
            }}
          >
            <FilterOptionCheckbox state={allSelected ? 'included' : 'off'} value={`all-${group.value}`} />
            <span className="truncate">Select all</span>
          </div>
        )}
        {group.options.map(renderOptionRow)}
      </div>
    </FilterOptionCard>
  );
}
